/**
 * Free Sampler Selector — Bean Block
 *
 * Custom element <free-sampler-selector> rendered in the mini-cart section.
 * Re-initializes on every section re-render (DOM replacement) via connectedCallback.
 *
 * Eligibility is determined in this order:
 *   1. Liquid-provided `data-qualifying-variant-ids` (from the 250g Coffee Beans collection)
 *   2. If empty → fetch /collections/250g-coffee-beans/products.json client-side
 *   3. Cart items are read from the DOM ([data-variant-id] on <li> elements)
 *   4. A cart item qualifies if its variant ID is in the qualifying list AND it's not a sampler variant
 *
 * Sampler state (already added or not) comes from Liquid data attributes:
 *   data-current-sampler-id, data-current-sampler-name, data-current-sampler-line
 */

class FreeSamplerSelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // --- Read data attributes (set by Liquid) ---
    this.qualifyingAttr = (this.dataset.qualifyingVariantIds || '').trim();

    try {
      this.samplers = JSON.parse(this.dataset.samplers || '[]');
    } catch (e) {
      this.samplers = [];
    }

    // Sampler variant IDs (hardcoded from data-samplers)
    this.samplerVariantIds = new Set(this.samplers.map(function (s) { return String(s.id); }));

    // Already-added sampler state (from Liquid cart inspection, always reliable)
    this.currentSamplerId = (this.dataset.currentSamplerId || '').trim();
    this.currentSamplerName = (this.dataset.currentSamplerName || '').trim();
    this.currentSamplerLine = (this.dataset.currentSamplerLine || '').trim();

    this.miniCart = document.querySelector('mini-cart');

    // --- Build qualifying variant ID set ---
    this.qualifyingIds = this.parseQualifyingIds(this.qualifyingAttr);

    var self = this;
    if (this.qualifyingIds.size === 0) {
      // Fallback: try fetching collection products via AJAX
      this.fetchQualifyingFromCollection().then(function (ids) {
        self.qualifyingIds = ids;
        self.render();
      }).catch(function () {
        // Still empty — render will handle it (hide panel)
        console.warn('FreeSampler: No qualifying 250g variant IDs found. Make sure the "250g Coffee Beans" collection exists and has products. If the Liquid lookup fails, add qualifying variant IDs to the hardcoded fallback in free-sampler.js.');
        self.render();
      });
    } else {
      this.render();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Parse qualifying IDs from string + fallback                        */
  /* ------------------------------------------------------------------ */

  parseQualifyingIds(str) {
    var ids = new Set();
    if (!str) return ids;
    str.split(',').forEach(function (id) {
      id = id.trim();
      if (id) ids.add(id);
    });
    return ids;
  }

  /**
   * Fetch products from the 250g Coffee Beans collection via Shopify's public JSON endpoint.
   * Returns a Promise<Set> of qualifying variant IDs.
   */
  fetchQualifyingFromCollection() {
    var self = this;
    return new Promise(function (resolve) {
      var url = '/collections/250g-coffee-beans/products.json?limit=250';
      fetch(url)
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function (data) {
          var ids = new Set();
          if (data && data.products) {
            data.products.forEach(function (product) {
              (product.variants || []).forEach(function (variant) {
                ids.add(String(variant.id));
              });
            });
          }
          resolve(ids);
        })
        .catch(function () {
          resolve(new Set()); // empty fallback
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Detect eligibility from DOM cart items                             */
  /* ------------------------------------------------------------------ */

  getCartItemVariantIds() {
    // Look for cart item <li> elements with data-variant-id
    var cartItemsEl = this.closest('.mini-cart__main') || document;
    var items = cartItemsEl.querySelectorAll('[data-variant-id]');
    var ids = [];
    items.forEach(function (el) {
      var vid = (el.getAttribute('data-variant-id') || '').trim();
      if (vid) ids.push(vid);
    });
    return ids;
  }

  hasQualifyingItem() {
    var cartVariantIds = this.getCartItemVariantIds();
    var qualifyingIds = this.qualifyingIds;

    // If we have no qualifying IDs, we can't match — feature can't work
    if (qualifyingIds.size === 0) return false;

    for (var i = 0; i < cartVariantIds.length; i++) {
      var vid = cartVariantIds[i];
      // Skip sampler variants
      if (this.samplerVariantIds.has(vid)) continue;
      // Check if it's a qualifying 250g variant
      if (qualifyingIds.has(vid)) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */

  render() {
    // Case 1: Sampler already in cart (detected by Liquid via _bb_free_sampler property)
    if (this.currentSamplerId) {
      this.removeAttribute('hidden');
      this.renderAddedState();
      return;
    }

    // Case 2: Orphaned sampler (Liquid says sampler in cart but no qualifying — shouldn't happen with auto-remove)
    // This is handled by the else branch: if no qualifying and no sampler, hide.

    // Case 3: Check eligibility from the DOM
    if (!this.hasQualifyingItem()) {
      this.setAttribute('hidden', '');
      this.innerHTML = '';
      return;
    }

    // Case 4: Qualifying items present → show selection grid
    this.removeAttribute('hidden');
    this.renderSelectionGrid();
  }

  /* ------------------------------------------------------------------ */
  /*  Selection grid                                                     */
  /* ------------------------------------------------------------------ */

  renderSelectionGrid() {
    var self = this;

    var cardsHtml = this.samplers
      .map(function (sampler) {
        return (
          '<button type="button" class="free-sampler__card" ' +
          'data-sampler-id="' + sampler.id + '" ' +
          'data-sampler-name="' + self.escapeAttr(sampler.name) + '" ' +
          'aria-pressed="false" ' +
          'aria-label="Select ' + self.escapeAttr(sampler.name) + '">' +
          '<span class="free-sampler__card-name">' + self.escapeHtml(sampler.name) + '</span>' +
          '<span class="free-sampler__card-notes">' + self.escapeHtml(sampler.notes) + '</span>' +
          '</button>'
        );
      })
      .join('');

    this.innerHTML =
      '<div class="free-sampler">' +
      '  <div class="free-sampler__header">' +
      '    <span class="free-sampler__badge">Website Exclusive</span>' +
      '    <p class="free-sampler__title">Your order qualifies for 1 free coffee sampler.</p>' +
      '    <p class="free-sampler__subtitle">Choose one to include with your order.</p>' +
      '  </div>' +
      '  <div class="free-sampler__grid" role="radiogroup" aria-label="Choose a free sampler">' +
      cardsHtml +
      '  </div>' +
      '  <div class="free-sampler__actions">' +
      '    <button type="button" class="free-sampler__button free-sampler__button--primary" ' +
      '      id="FreeSamplerAdd" disabled aria-disabled="true">' +
      '      Add Free Sampler' +
      '    </button>' +
      '  </div>' +
      '  <div class="free-sampler__error" id="FreeSamplerError" role="alert">' +
      '    <span class="free-sampler__error-icon" aria-hidden="true">!</span>' +
      '    <span class="free-sampler__error-text"></span>' +
      '  </div>' +
      '</div>';

    this.bindSelectionEvents();
  }

  bindSelectionEvents() {
    var self = this;
    var cards = this.querySelectorAll('.free-sampler__card');
    var addButton = this.querySelector('#FreeSamplerAdd');
    var errorEl = this.querySelector('#FreeSamplerError');

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        // Deselect all
        cards.forEach(function (c) {
          c.classList.remove('free-sampler__card--selected');
          c.setAttribute('aria-pressed', 'false');
        });
        // Select this one
        card.classList.add('free-sampler__card--selected');
        card.setAttribute('aria-pressed', 'true');
        // Enable add button
        addButton.disabled = false;
        addButton.removeAttribute('aria-disabled');
        // Store selection
        self.selectedSamplerId = card.dataset.samplerId;
        self.selectedSamplerName = card.dataset.samplerName;
        // Hide error
        errorEl.classList.remove('is-visible');
      });
    });

    addButton.addEventListener('click', function () {
      self.handleAddSampler();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Added state                                                        */
  /* ------------------------------------------------------------------ */

  renderAddedState() {
    this.innerHTML =
      '<div class="free-sampler">' +
      '  <div class="free-sampler__header">' +
      '    <span class="free-sampler__badge">Website Exclusive</span>' +
      '    <p class="free-sampler__title">Your order qualifies for 1 free coffee sampler.</p>' +
      '  </div>' +
      '  <div class="free-sampler__added">' +
      '    <span class="free-sampler__added-icon" aria-hidden="true">\u2713</span>' +
      '    <span class="free-sampler__added-text">' +
      '      Free sampler added: <span class="free-sampler__added-name">' +
      this.escapeHtml(this.currentSamplerName) +
      '    </span></span>' +
      '  </div>' +
      '  <div class="free-sampler__actions">' +
      '    <button type="button" class="free-sampler__button free-sampler__button--secondary" ' +
      '      id="FreeSamplerChange">' +
      '      Change selection' +
      '    </button>' +
      '  </div>' +
      '  <div class="free-sampler__error" id="FreeSamplerError" role="alert">' +
      '    <span class="free-sampler__error-icon" aria-hidden="true">!</span>' +
      '    <span class="free-sampler__error-text"></span>' +
      '  </div>' +
      '</div>';

    this.bindAddedEvents();
  }

  bindAddedEvents() {
    var self = this;
    var changeButton = this.querySelector('#FreeSamplerChange');

    if (changeButton) {
      changeButton.addEventListener('click', function () {
        self.handleChangeSampler();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Add sampler                                                        */
  /* ------------------------------------------------------------------ */

  handleAddSampler() {
    var self = this;

    if (!this.selectedSamplerId) return;

    var addButton = this.querySelector('#FreeSamplerAdd');
    var errorEl = this.querySelector('#FreeSamplerError');

    if (addButton) {
      addButton.classList.add('is-loading');
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');
    }
    if (errorEl) {
      errorEl.classList.remove('is-visible');
    }

    var sections = this.getSectionIds();
    var body = JSON.stringify({
      id: this.selectedSamplerId,
      quantity: 1,
      properties: {
        _bb_free_sampler: true
      },
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: body
    })
      .then(function (response) { return response.json(); })
      .then(function (parsedState) {
        if (parsedState.status === 422) {
          self.showError(parsedState.description || 'Could not add sampler. Please try again.');
          if (addButton) {
            addButton.classList.remove('is-loading');
            addButton.disabled = false;
            addButton.removeAttribute('aria-disabled');
          }
          return;
        }
        // Success — re-render cart drawer
        if (self.miniCart) {
          self.miniCart.renderContents(parsedState);
        }
      })
      .catch(function () {
        self.showError('Something went wrong. Please try again.');
        if (addButton) {
          addButton.classList.remove('is-loading');
          addButton.disabled = false;
          addButton.removeAttribute('aria-disabled');
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Change sampler (remove existing, then selection reappears)         */
  /* ------------------------------------------------------------------ */

  handleChangeSampler() {
    var self = this;

    var changeButton = this.querySelector('#FreeSamplerChange');
    var errorEl = this.querySelector('#FreeSamplerError');

    if (changeButton) {
      changeButton.classList.add('is-loading');
      changeButton.disabled = true;
    }
    if (errorEl) {
      errorEl.classList.remove('is-visible');
    }

    var line = parseInt(this.currentSamplerLine, 10);
    if (!line || isNaN(line)) {
      this.showError('Could not find sampler in cart. Please refresh.');
      if (changeButton) {
        changeButton.classList.remove('is-loading');
        changeButton.disabled = false;
      }
      return;
    }

    var sections = this.getSectionIds();
    var body = JSON.stringify({
      line: line,
      quantity: 0,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: body
    })
      .then(function (r) { return r.text(); })
      .then(function (state) {
        var parsedState = JSON.parse(state);
        if (parsedState.errors) {
          self.showError('Could not change sampler. Please try again.');
          if (changeButton) {
            changeButton.classList.remove('is-loading');
            changeButton.disabled = false;
          }
          return;
        }
        // Success — re-render shows selection grid again
        if (self.miniCart) {
          self.miniCart.renderContents(parsedState);
        }
      })
      .catch(function () {
        self.showError('Something went wrong. Please try again.');
        if (changeButton) {
          changeButton.classList.remove('is-loading');
          changeButton.disabled = false;
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  getSectionIds() {
    if (this.miniCart && typeof this.miniCart.getSectionsToRender === 'function') {
      return this.miniCart.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  showError(message) {
    var errorEl = this.querySelector('#FreeSamplerError');
    if (!errorEl) return;
    var textEl = errorEl.querySelector('.free-sampler__error-text');
    if (textEl) {
      textEl.textContent = message;
    }
    errorEl.classList.add('is-visible');
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

customElements.define('free-sampler-selector', FreeSamplerSelector);
